'use client';

import { useState, useEffect, useMemo } from 'react';
import Link from 'next/link';
import { ArrowLeftIcon } from 'lucide-react';
import { BlogPost, isPublished } from '../../../_data/posts.types';
import {
  BLOG_DECISION_FRAMEWORK_MARKER,
  getDecisionFrameworkBySlug,
} from '../_data/decision-framework';
import {
  BLOG_POST_FAQ_MARKER,
  type IBlogFaqItem,
  getBlogFaqBySlug,
} from '../_data/blog-post-faq';
import {
  BLOG_CONVERSION_CTA_MARKER,
  resolveBlogConversionCtaConfig,
} from '../_data/blog-conversion-cta-mount';
import BlogConversionCtaSection from './BlogConversionCtaSection';
import BlogTableOfContents from './BlogTableOfContents';
import DecisionFrameworkSection from './DecisionFrameworkSection';
import FAQSection from '@/app/_components/main/FAQSection';
import type { IDecisionFrameworkConfig } from '../_data/decision-framework';
import { trackBlogCtaClick as trackBlogCtaLocationClick, type TBlogCtaLocation } from '@/app/_utils/analytics';

interface IBlogPostContentProps {
  post: BlogPost;
  postSlug: string;
  children: React.ReactNode;
}

type TArticleBody =
  | { mode: 'single'; html: string }
  | {
      mode: 'df';
      beforeHtml: string;
      tailHtml: string;
      config: IDecisionFrameworkConfig;
    }
  | {
      mode: 'dfFaq';
      beforeHtml: string;
      config: IDecisionFrameworkConfig;
      /** Optional HTML between decision mount and FAQ mount (usually empty) */
      betweenDecisionAndFaqHtml: string;
      faqItems: IBlogFaqItem[];
      tailAfterFaqHtml: string;
    };

const BlogPostContent = ({ post, postSlug, children }: IBlogPostContentProps) => {
  const [mounted, setMounted] = useState(false);
  const [showComingSoon, setShowComingSoon] = useState(false);

  useEffect(() => {
    queueMicrotask(() => {
      setMounted(true);
      setShowComingSoon(!isPublished(post.meta));
    });
  }, [post.meta]);

  useEffect(() => {
    const elements = Array.from(
      document.querySelectorAll<HTMLAnchorElement>('.article-content a[data-blog-cta-location]'),
    );

    const handlers = elements.map((el) => {
      const handler = () => {
        const location = el.dataset.blogCtaLocation as TBlogCtaLocation | undefined;
        const page = el.dataset.blogCtaPage || postSlug;
        if (!location || !['top', 'mid', 'bottom'].includes(location)) return;

        trackBlogCtaLocationClick({
          location,
          page,
        });
      };
      el.addEventListener('click', handler);
      return { el, handler };
    });

    return () => {
      handlers.forEach(({ el, handler }) => {
        el.removeEventListener('click', handler);
      });
    };
  }, [postSlug, post.content]);

  const articleBody = useMemo((): TArticleBody => {
    const raw = post.content;
    const faqItems = getBlogFaqBySlug(postSlug);

    if (!raw.includes(BLOG_DECISION_FRAMEWORK_MARKER)) {
      return { mode: 'single', html: raw };
    }

    const dfParts = raw.split(BLOG_DECISION_FRAMEWORK_MARKER);
    if (dfParts.length !== 2) {
      return { mode: 'single', html: raw };
    }

    const dfConfig = getDecisionFrameworkBySlug(postSlug);
    if (!dfConfig) {
      return { mode: 'single', html: dfParts[0] + dfParts[1] };
    }

    const afterDecision = dfParts[1];

    if (faqItems?.length && afterDecision.includes(BLOG_POST_FAQ_MARKER)) {
      const faqParts = afterDecision.split(BLOG_POST_FAQ_MARKER);
      if (faqParts.length === 2) {
        return {
          mode: 'dfFaq',
          beforeHtml: dfParts[0],
          config: dfConfig,
          betweenDecisionAndFaqHtml: faqParts[0] ?? '',
          faqItems,
          tailAfterFaqHtml: faqParts[1] ?? '',
        };
      }
    }

    return {
      mode: 'df',
      beforeHtml: dfParts[0],
      tailHtml: afterDecision,
      config: dfConfig,
    };
  }, [post.content, postSlug]);

  if (!mounted) {
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <div className='animate-pulse text-muted-foreground'>Loading...</div>
      </div>
    );
  }

  if (showComingSoon) {
    return (
      <div className='min-h-screen flex flex-col gap-1 items-center justify-center text-center px-6'>
        <p className='text-sm uppercase tracking-widest text-primary mb-4'>Upcoming Article</p>
        <h1 className='text-3xl md:text-5xl font-bold mb-2 pb-1 gradient-strong'>Coming Soon</h1>
        <p className='text-muted-foreground max-w-md mb-8'>
          This article is scheduled to be published soon. Stay tuned.
        </p>
        <Link
          href='/blog'
          className='inline-flex items-center gap-2 text-accent font-semibold transition-opacity duration-300 hover:opacity-80'
          aria-label='Return to blog list'
        >
          <ArrowLeftIcon className='w-4 h-4 shrink-0' aria-hidden='true' />
          Back to Blog
        </Link>
      </div>
    );
  }

  const renderHtmlWithConversionCta = (rawHtml: string, keyPrefix: string) => {
    if (!rawHtml.includes(BLOG_CONVERSION_CTA_MARKER)) {
      return <div className='blog-article-html' dangerouslySetInnerHTML={{ __html: rawHtml }} />;
    }

    const [before, after] = rawHtml.split(BLOG_CONVERSION_CTA_MARKER);
    const ctaConfig = resolveBlogConversionCtaConfig(postSlug);

    return (
      <>
        {before ? (
          <div
            key={`${keyPrefix}-before`}
            className='blog-article-html'
            dangerouslySetInnerHTML={{ __html: before }}
          />
        ) : null}
        <BlogConversionCtaSection config={ctaConfig} pageSlug={postSlug} />
        {after ? (
          <div
            key={`${keyPrefix}-after`}
            className='blog-article-html'
            dangerouslySetInnerHTML={{ __html: after }}
          />
        ) : null}
      </>
    );
  };

  const renderArticleMain = () => {
    if (articleBody.mode === 'single') {
      return renderHtmlWithConversionCta(articleBody.html, 'single');
    }

    if (articleBody.mode === 'df') {
      return (
        <>
          <div className='blog-article-html' dangerouslySetInnerHTML={{ __html: articleBody.beforeHtml }} />
          <DecisionFrameworkSection
            config={articleBody.config}
            analyticsPageSlug={postSlug}
            analyticsSection='bottom_cta'
          />
          {renderHtmlWithConversionCta(articleBody.tailHtml, 'df-tail')}
        </>
      );
    }

    const between = articleBody.betweenDecisionAndFaqHtml.trim();
    const startupFaqIndex = articleBody.faqItems.findIndex((x) => x.id === 'best-tool-startups');

    return (
      <>
        <div className='blog-article-html' dangerouslySetInnerHTML={{ __html: articleBody.beforeHtml }} />
        <DecisionFrameworkSection
          config={articleBody.config}
          analyticsPageSlug={postSlug}
          analyticsSection='bottom_cta'
        />
        {between ? (
          renderHtmlWithConversionCta(between, 'df-faq-between')
        ) : null}
        <FAQSection
          variant='blog'
          items={articleBody.faqItems.map(({ question, answer }) => ({ question, answer }))}
          defaultOpenIndex={startupFaqIndex >= 0 ? startupFaqIndex : null}
        />
        {renderHtmlWithConversionCta(articleBody.tailAfterFaqHtml, 'df-faq-tail')}
      </>
    );
  };

  return (
    <>
      <article className=''>
        <div className='relative my-12 md:my-14'>
          <Link
            href={`/blog/${post.categorySlug}`}
            className='inline-block text-xs font-semibold uppercase tracking-widest text-blog-link border border-border/50 rounded-full px-4 py-2 mb-4 hover:bg-[hsl(var(--blog-link)/0.1)] hover:text-[hsl(var(--blog-link-hover))] transition-colors duration-200'
          >
            {post.category}
          </Link>
          <h1 className='text-[clamp(2.24rem,4.8vw,3.6rem)] font-bold leading-[1.1] mb-6 text-foreground tracking-[-0.03em]'>
            {post.title}
          </h1>
          <div className='flex gap-6 text-sm text-muted-foreground items-center'>
            <div className='flex gap-2'>
              {post.meta.publishedDate ? (
                <time dateTime={new Date(post.meta.publishedDate).toISOString()}>
                  {new Date(post.meta.publishedDate).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                  })}
                </time>
              ) : (
                (post.meta.year ?? '')
              )}
            </div>
            <div className='flex gap-2 before:content-[""] before:w-[4px] before:h-[4px] before:bg-muted-foreground before:rounded-full before:inline-block'>
              {post.meta.author}
            </div>
          </div>
        </div>
        <BlogTableOfContents contentKey={post.content} />
        <div className='article-content'>{renderArticleMain()}</div>
      </article>

      {children}
    </>
  );
};

export default BlogPostContent;
